import React, { useCallback } from "react";
import {
  Button,
  ButtonGroup,
  Col,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  Label,
  Spinner,
} from "reactstrap";

import { Dispatch, State } from "../StateManager/state";

type Props = Readonly<{
  dispatch: Dispatch;
  onReset: () => void;
  onSave: () => void;
  onSubmit: () => void;
  pageModified: boolean;
  readonly: boolean;
  saving: boolean;
  submitting: boolean;
  state: State;
}>;

export const JobActions = ({
  dispatch,
  onReset,
  onSave,
  onSubmit,
  pageModified,
  readonly,
  saving,
  submitting,
  state,
}: Props) => {
  let zoom = state.zoom;
  if (state.editTable) {
    zoom = state.tableZoom;
  }
  const decreaseZoom = useCallback(() => {
    if (zoom <= 1) {
      return;
    }
    dispatch({
      type: "CHANGE ZOOM LEVEL",
      zoom: zoom - 0.5,
    });
  }, [dispatch, zoom]);
  const increaseZoom = useCallback(() => {
    if (zoom >= 3) {
      return;
    }
    dispatch({
      type: "CHANGE ZOOM LEVEL",
      zoom: zoom + 0.5,
    });
  }, [dispatch, zoom]);
  let saveContents: React.ReactNode = "Save";
  if (saving) {
    saveContents = (
      <>
        Saving
        <Spinner className="ml-2" size="sm" />
      </>
    );
  } else if (!pageModified) {
    saveContents = (
      <>
        Saved
        <i className="fa fa-check ml-3" />
      </>
    );
  }
  let submitContents: React.ReactNode = "Submit";
  if (submitting) {
    submitContents = (
      <>
        Submit
        <Spinner className="ml-2" size="sm" />
      </>
    );
  }
  return (
    <Form
      className="job-actions"
      onSubmit={e => {
        e.preventDefault();
      }}
    >
      <FormGroup row className={readonly ? "mb-0" : ""}>
        <Label for="zoom-level" sm={4}>
          Zoom:
        </Label>
        <Col sm={8}>
          <InputGroup>
            <InputGroupAddon addonType="prepend">
              <Button
                color="secondary"
                disabled={zoom <= 1}
                onClick={decreaseZoom}
                style={{ cursor: "zoom-out" }}
                title="Zoom out"
                type="button"
              >
                <i className="fa fa-search-minus" />
              </Button>
            </InputGroupAddon>
            <Input
              readOnly
              style={{ pointerEvents: "none", textAlign: "center" }}
              type="text"
              value={`${zoom * 100}%`}
            />
            <InputGroupAddon addonType="append">
              <Button
                color="secondary"
                disabled={zoom >= 3}
                onClick={increaseZoom}
                style={{ cursor: "zoom-in" }}
                title="Zoom in"
                type="button"
              >
                <i className="fa fa-search-plus" />
              </Button>
            </InputGroupAddon>
          </InputGroup>
        </Col>
      </FormGroup>
      {!readonly && (
        <FormGroup className="mb-0">
          <ButtonGroup className="w-100">
            <Button
              block
              color="danger"
              disabled={saving || submitting}
              onClick={onReset}
              size="sm"
              type="button"
            >
              Reset Page
            </Button>
            <Button
              block
              color="success"
              disabled={saving || submitting || !pageModified}
              onClick={onSave}
              size="sm"
              type="button"
            >
              {saveContents}
            </Button>
            <Button
              block
              color="primary"
              disabled={saving || submitting}
              onClick={onSubmit}
              size="sm"
              type="submit"
            >
              {submitContents}
            </Button>
          </ButtonGroup>
        </FormGroup>
      )}
    </Form>
  );
};
JobActions.displayName = "JobActions";
